import React, { useState } from 'react';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';
import PageContainer from '../components/common/PageContainer';
import PageSection from '../components/common/PageSection';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../shared/config/src';

const WellnessPage: React.FC = () => {
  const { themeClasses } = useTheme();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);

  // Form state
  const [sleepHours, setSleepHours] = useState<number>(8);
  const [sleepQuality, setSleepQuality] = useState<string>('Good');
  const [didExercise, setDidExercise] = useState<boolean | null>(null);
  const [exerciseType, setExerciseType] = useState<string>('');
  const [exerciseDuration, setExerciseDuration] = useState<number>(30);
  const [meals, setMeals] = useState<string>('3');
  const [eatingPerception, setEatingPerception] = useState<string>('Balanced');
  const [energyLevel, setEnergyLevel] = useState<number>(7);
  const [currentStressors, setCurrentStressors] = useState<string>('');
  const [futureStress, setFutureStress] = useState<string>('');
  const [personalGoals, setPersonalGoals] = useState<string>('');

  const isFormValid = didExercise !== null;

  const handleSave = async () => {
    if (!user) {
      console.error('User not logged in');
      return;
    }

    if (!isFormValid) {
        console.error('Form is not valid');
        return;
    }

    setIsSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
      const wellnessLogRef = doc(db, `users/${user.uid}/wellness_logs`, today);
      
      const wellnessData = {
        uid: user.uid,
        createdAt: new Date(),
        sleep: { hours: sleepHours, quality: sleepQuality },
        exercise: { exercised: didExercise, type: didExercise ? exerciseType : null, duration: didExercise ? exerciseDuration : null },
        eating: { meals: meals, perception: eatingPerception },
        energyLevel: energyLevel,
        stress: { current: currentStressors, future: futureStress },
        goals: personalGoals,
      };

      await setDoc(wellnessLogRef, wellnessData, { merge: true });
      // In a real app, show a success toast
    } catch (error) {
      console.error('Error saving wellness data:', error);
      // In a real app, show an error toast
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <PageContainer>
      <div className="max-w-3xl mx-auto">
        <PageSection title="Wellness Check-in" emoji="🧘‍♀️">
          <p className="text-gray-600 mb-6">
            Track your daily habits to understand your well-being better. Your entries will help provide more personalized insights.
          </p>
        </PageSection>
        
        <div className="space-y-6">
            {/* Sleep Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Sleep</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Hours of Sleep</label>
                        <input
                            type="number"
                            min="0"
                            max="24"
                            value={sleepHours}
                            onChange={(e) => setSleepHours(Number(e.target.value))}
                            className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Sleep Quality</label>
                        <select
                            value={sleepQuality}
                            onChange={(e) => setSleepQuality(e.target.value)}
                            className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="Poor">Poor</option>
                            <option value="Fair">Fair</option>
                            <option value="Good">Good</option>
                            <option value="Excellent">Excellent</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Exercise Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Exercise</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Did you exercise today?</label>
                        <div className="flex space-x-4">
                            <button
                                onClick={() => setDidExercise(true)}
                                className={`px-4 py-2 rounded-lg ${
                                    didExercise === true ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                Yes
                            </button>
                            <button
                                onClick={() => setDidExercise(false)}
                                className={`px-4 py-2 rounded-lg ${
                                    didExercise === false ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                No
                            </button>
                        </div>
                    </div>
                    {didExercise && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Type of Exercise</label>
                                <input
                                    type="text"
                                    value={exerciseType}
                                    onChange={(e) => setExerciseType(e.target.value)}
                                    placeholder="e.g., Running, Yoga, Weightlifting"
                                    className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Duration (minutes)</label>
                                <input
                                    type="number"
                                    min="0"
                                    value={exerciseDuration}
                                    onChange={(e) => setExerciseDuration(Number(e.target.value))}
                                    className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Eating Habits Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Eating Habits</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Number of Meals</label>
                        <select
                            value={meals}
                            onChange={(e) => setMeals(e.target.value)}
                            className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="1">1</option>
                            <option value="2">2</option>
                            <option value="3">3</option>
                            <option value="4">4</option>
                            <option value="5+">5+</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Eating Perception</label>
                        <select
                            value={eatingPerception}
                            onChange={(e) => setEatingPerception(e.target.value)}
                            className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="Poor">Poor</option>
                            <option value="Fair">Fair</option>
                            <option value="Balanced">Balanced</option>
                            <option value="Excellent">Excellent</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Energy Level Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Energy Level</h3>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Rate your energy level (1-10)</label>
                    <div className="flex items-center space-x-2">
                        <span className="text-sm text-gray-600">1</span>
                        <input
                            type="range"
                            min="1"
                            max="10"
                            value={energyLevel}
                            onChange={(e) => setEnergyLevel(Number(e.target.value))}
                            className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                        />
                        <span className="text-sm text-gray-600">10</span>
                        <span className="text-lg font-semibold text-blue-600 w-8">{energyLevel}</span>
                    </div>
                </div>
            </div>

            {/* Stress & Goals Section */}
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Stress & Goals</h3>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Current Stressors</label>
                        <textarea
                            value={currentStressors}
                            onChange={(e) => setCurrentStressors(e.target.value)}
                            placeholder="What's causing you stress right now?"
                            className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={3}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Future Stress Concerns</label>
                        <textarea
                            value={futureStress}
                            onChange={(e) => setFutureStress(e.target.value)}
                            placeholder="What are you worried about for the future?"
                            className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={3}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Personal Goals</label>
                        <textarea
                            value={personalGoals}
                            onChange={(e) => setPersonalGoals(e.target.value)}
                            placeholder="What are your current personal goals?"
                            className="w-full p-3 border border-gray-300 bg-white text-gray-900 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            rows={3}
                        />
                    </div>
                </div>
            </div>
        </div>

        <div className="mt-6">
            <button
                onClick={handleSave}
                disabled={isSaving || !isFormValid}
                className={`w-full py-2 px-4 rounded-md text-white font-semibold ${
                    isFormValid 
                        ? 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500' 
                        : 'bg-gray-300 cursor-not-allowed'
                }`}
            >
                {isSaving ? 'Saving...' : 'Save Entry'}
            </button>
        </div>
      </div>
    </PageContainer>
  );
};

export default WellnessPage; 